import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAuth } from "../../../../auth/useAuth";
import { useProductDetailContext } from "../../../../hooks/useProductDetailContext";
import CatalogContext from "../../../../contexts/CatalogContext";
import { Scanner } from "@yudiel/react-qr-scanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCameraRotate } from '@fortawesome/pro-solid-svg-icons';

import "./ScannerButton.scss";
import { NO_PRODUCT_FOUND_AUTO_CLOSE_TIMEOUT_MILLIS } from "../item/ProductDetail";
import { useProposalContext } from "../../../../hooks/useProposalContext";
import { SidebarContentType, useSidebarContext } from "../../../../hooks/useSidebarContext";
import { Cache } from "../../../../util/Cache.util";

const PRODUCT_SCANNER_INSTRUCTIONS_DISMISSED_KEY = "product_scanner.instructions.dismissed";

const SCANNER_BARCODE_FORMATS = [
	'qr_code',
	'micro_qr_code',
	'rm_qr_code',
	'maxi_code',
	'pdf417',
	'aztec',
	'data_matrix',
	'matrix_codes',
	'dx_film_edge',
	'databar',
	'databar_expanded',
	'codabar',
	'code_39',
	'code_93',
	'code_128',
	'ean_8',
	'ean_13',
	'itf',
	'linear_codes',
	'upc_a',
	'upc_e'
];

export default function ScannerButton() {
	const { currentUser } = useAuth();
	const { modalProps, setModalProps } = useProductDetailContext();

	const [catalog] = React.useContext(CatalogContext);
	const [showScanner, setShowScanner] = React.useState(false);
	const last_scan = React.useRef(undefined);
	const scanner_container_ref = React.useRef(null);
	const [enableCamera, setEnableCamera] = React.useState(false);
	const instructionsRef = React.useRef();
	const dismissRef = React.useRef();
	const [deviceIdOverride, setDeviceIdOverride] = React.useState(undefined);
	const [alternateCameras, setAlternateCameras] = React.useState([]);
	const [mediaAllowed, setMediaAllowed] = React.useState(undefined);
	const autoAddCheckboxRef = React.useRef(undefined);
	
	const ProposalContext = useProposalContext();
	const SidebarContext = useSidebarContext();
	
	const dismissedInstructions = !!localStorage.getItem(PRODUCT_SCANNER_INSTRUCTIONS_DISMISSED_KEY);

	const is_manscan = localStorage.getItem("manscan") === "true";

	// prevent close if instructions and/or dismiss are clicked
	const onDescriptionClick = React.useCallback(e => {
		e.stopPropagation();
	}, []);

	const onDismissChange = React.useCallback(e => {
		// just enough time to see the checkbox get checked before hiding
		if (e.target.checked) {
			setTimeout(() => instructionsRef.current?.classList.add("dismissed"), 250);
			localStorage.setItem(PRODUCT_SCANNER_INSTRUCTIONS_DISMISSED_KEY, "1");
		}
	}, []);

	// MOUNT
	React.useEffect(() => {
		const insRef = instructionsRef.current;
		const disRef = dismissRef.current;
		insRef?.addEventListener("click", onDescriptionClick);
		disRef?.addEventListener("change", onDismissChange);

		return () => {
			insRef?.removeEventListener("click", onDescriptionClick);
			disRef?.removeEventListener("change", onDismissChange);
		};
	}, [onDescriptionClick, onDismissChange, mediaAllowed]);

	React.useEffect(() => {
		videoCheck();
	}, [showScanner]);

	const videoCheck = async () => {
		if (showScanner && !alternateCameras?.length) {
			try {
				// get devices to kickstart permission; disabled if blocked. if not, grab other devices so they're prepped for allowing scanner
				const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
				stream.getVideoTracks().forEach(t => t.stop());

				navigator.mediaDevices.enumerateDevices()
					.then(devices => devices.filter(device => device.kind === "videoinput"))
					.then(devices => {
						setMediaAllowed(true);
						setAlternateCameras(devices);
					});
			}
			catch (e) {
				setShowScanner(false);
			}
		}

		if (!showScanner) {
			setDeviceIdOverride(undefined);
		}
	}

	React.useEffect(() => {
		// if item false, modal is closing; re-enable scanner if showing
		if (showScanner && !is_manscan) {
			if (modalProps.product_not_found) {
				setModalProps(current => ({...current, identifier: undefined, item: undefined}));
				setEnableCamera(true);
			}
			else {
				setEnableCamera(!modalProps.item);
			}
		}
	}, [modalProps.item, showScanner, modalProps.product_not_found, setEnableCamera, setModalProps]);

	const on_scan = React.useCallback(async scans => {
		let scan = scans.at(0);
		let result = scan.rawValue;
		// metadata will be undefined in manscan mode
		// 1D UPC barcode, strip checksum digit
		if (scan.format === "code_128") {
			result = result.substring(0, result.length - 1);
		}
		if (result && result !== last_scan.current) {
			last_scan.current = result;
			setTimeout(() => {
				last_scan.current = undefined;
			}, NO_PRODUCT_FOUND_AUTO_CLOSE_TIMEOUT_MILLIS + 1_000);
			let scan_result = result;

			if (typeof result === "number") {
				scan_result = result;
			}
			else if (typeof result === "string") {
				result = result.toLowerCase();
				if (result.startsWith("http")) {
					let scanned_url = new URL(result);
					// check for our qr code url ..../qr?url=[encoded url here]
					if (scanned_url.searchParams.has("url")) {
						const inner_url = new URL(scanned_url.searchParams.get("url"));
						if (inner_url.searchParams.has("product")) {
							scanned_url = inner_url;
						}
					}

					if (scanned_url.searchParams.has("product")) {
						scan_result = scanned_url.searchParams.get("product");
					}
				}
				else {
					// let through ... this might be a mfg sku, product detail will handle that
					scan_result = result;
				}
			}
			setEnableCamera(false);
			
			if (autoAddCheckboxRef.current?.checked && ProposalContext.currentProposal) {
				const item = ProposalContext.currentProposal.addFrom(scan_result);
				await item.save();
				setShowScanner(false);
				SidebarContext.open(SidebarContentType.Proposal);
				return;
			}
			const catalog_page_id_any = catalog.pages.find(p => p.type?.code === "item-gallery")?.id;
			setModalProps(current => ({
				...current,
				identifier: undefined,
				item: scan_result,
				fullscreen: true,
				item_attr_endpoint: catalog.detail_fieldset?.handle
					? "Fieldset/:" + catalog.detail_fieldset.handle + "/fields"
					: "Catalog_Page/" + catalog_page_id_any + "/attributes/details",
			}));
		}
	}, [setModalProps, catalog.detail_fieldset, catalog.pages, ProposalContext.currentProposal]);

	const on_click = React.useCallback(() => {
		if (is_manscan) {
			on_scan([ { rawValue: prompt("Scan Value:") } ]);
		}
		else {
			if (showScanner) {
				// disable camera after it's transitioned away
				const style = window.getComputedStyle(scanner_container_ref.current);
				const disableVideoDelay = style?.getPropertyValue("--hide-show-timing-millis") ?? 0;
				setTimeout(() => setEnableCamera(false), +disableVideoDelay);
			}

			setShowScanner(current => !current);
		}
	}, [setShowScanner, on_scan, setEnableCamera, showScanner]);

	const changeCamera_click = React.useCallback(e => {
		e.preventDefault();
		e.stopPropagation();

		if (!deviceIdOverride) {
			setDeviceIdOverride(alternateCameras.at(1)?.deviceId);
		}
		else {
			const currentCameraIndex = alternateCameras.findIndex(it => it.deviceId === deviceIdOverride);
			const newCamera = currentCameraIndex !== -1 ? (alternateCameras.at(currentCameraIndex + 1) ?? alternateCameras.at(0)) : undefined;
			setDeviceIdOverride(newCamera?.deviceId);
		}

	}, [alternateCameras, deviceIdOverride]);
	
	const autoAdd_click = e => {
		e.preventDefault();
		e.stopPropagation();
		autoAddCheckboxRef.current.checked = !autoAddCheckboxRef.current.checked;
		Cache.Session().set("scanner-auto-add", autoAddCheckboxRef.current.checked ? "true" : undefined);
	}
	
	const ProposalAutoAdd = () => {
		if (ProposalContext.currentProposal) {
			const checked = Cache.Session().get("scanner-auto-add");
			
			return (
				<div id="auto-add" onClick={autoAdd_click}>
					<label>
						<input type="checkbox" ref={autoAddCheckboxRef} checked={checked} onChange={() => {}} />
						Auto-Add to Proposal
					</label>
				</div>
			);
		}
	}
	
	
	return !currentUser ? (<></>) : (
		<div data-component="ScannerButton">
			<OverlayTrigger placement="bottom" overlay={
				<Tooltip className="_boss tooltip">Product Scanner</Tooltip>
			}>
				<Button
					type="button"
					variant="link"
					className="scanner-nav"
					id="scanner-nav-button"
					onClick={on_click}
				>
					<i className={["fa-light", "fa-qrcode"].join(" ")}></i>
				</Button>
			</OverlayTrigger>
			<div id="product-scanner" ref={scanner_container_ref} onClick={on_click}>
				<div id="scanner-backdrop"></div>
				<div id="scanner-container" className={[showScanner ? "show" : ""].join(" ")}>
					<div id="scanner-area">
						{ mediaAllowed && (
							<Scanner
								constraints={{ deviceId: deviceIdOverride, facingMode: { ideal: "environment" } }}
								components={{ audio: false, torch: false }}
								onScan={on_scan}
								formats={SCANNER_BARCODE_FORMATS}
								allowMultiple={false}
								paused={!enableCamera} />
						)}

						{ alternateCameras.length > 1 && (
							<Button id="change-camera" onClick={changeCamera_click}>
								<FontAwesomeIcon icon={faCameraRotate} />
							</Button>
						)}
					</div>
					
					<ProposalAutoAdd />
					
					{
						!dismissedInstructions && mediaAllowed && (
							<div id="qr-instructions" ref={instructionsRef}>
								<h3 className="iconable">
									Product Scanner
								</h3>
								<div id="description">
									A quick and convenient way to access additional information about products while shopping or researching.
								</div>
								<div id="explanation">
									<div>
										Scan a barcode or QR code for SKU, UPC, or Product Number.
									</div>
									<div>
										The scanner will automatically detect the code on screen and display product details.
									</div>
								</div>
								<div id="dismiss">
									<input id="dismiss-description" ref={dismissRef} type="checkbox" />
									<label htmlFor="dismiss-description">Don't show me this again</label>
								</div>
							</div>
						)
					}
				</div>
			</div>
		</div>
	);
}
